"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.commonPhoneNumberVersion = exports.getCallablePhoneNumber = exports.formatPhoneNumber = exports.parsePhoneNumber = exports.defaultPhoneNumberConfig = void 0;
const google_libphonenumber_1 = __importDefault(require("google-libphonenumber"));
const PhoneNumberUtil = google_libphonenumber_1.default.PhoneNumberUtil.getInstance();
const PhoneNumberFormat = google_libphonenumber_1.default.PhoneNumberFormat;
/**
 * Defines default configuration for parsing/formatting phone numbers,
 * so that developer would not need to pass it with each function call.
 */
exports.defaultPhoneNumberConfig = {
    regionCode: 'US',
};
/**
 * Parses any phone number to E164 format and preserved extension if it exists.
 * Uses regionCode from passed or default config as a hint when parsing, if for example country code is not supplied.
 * Parsed number is suitable for use in Alert and Notify services.
 *
 * @param {string} phoneNumber - Phone number to parse.
 * @param {PhoneNumberConfig} config - Configuration for parsing. If not passed {@link defaultPhoneNumberConfig} is used.
 * @returns {string|undefined} Number formatted in E164 (with extension support), undefined if phone number is invalid.
 * @example
 * Phone number in E164 with extension (E164 standard does not cover phone extensions):
 * '500-555-0006 ext. 1234' would result in '+15005550006x1234'
 */
function parsePhoneNumber(phoneNumber, config) {
    try {
        const parsedPhoneNumber = PhoneNumberUtil.parse(phoneNumber, (config !== null && config !== void 0 ? config : exports.defaultPhoneNumberConfig).regionCode);
        const valid = PhoneNumberUtil.isValidNumber(parsedPhoneNumber);
        if (!valid) {
            return undefined;
        }
        const phoneNumberExtension = parsedPhoneNumber.getExtension();
        return (PhoneNumberUtil.format(parsedPhoneNumber, PhoneNumberFormat.E164) +
            (phoneNumberExtension != null ? 'x' + phoneNumberExtension : ''));
    }
    catch (_a) {
        return undefined;
    }
}
exports.parsePhoneNumber = parsePhoneNumber;
/**
 * Formats to local format using the specified regionCode.
 * Returns unmodified phone number if phone number is invalid.
 *
 * @param {string} phoneNumber - Phone number in E164 format.
 * @param {string} config - Configuration for formatting. If not passed {@link defaultPhoneNumberConfig} is used.
 * @returns {string} Number formatted according to region specific local settings.
 * @example
 * Phone number in E164 with extension (E164 standard does not cover phone extensions):
 * '+41551234567', { regionCode: 'CH' } would result in '055 123 45 67'
 */
function formatPhoneNumber(phoneNumber, config) {
    try {
        const parsedPhoneNumber = PhoneNumberUtil.parse(phoneNumber, (config !== null && config !== void 0 ? config : exports.defaultPhoneNumberConfig).regionCode);
        if (!PhoneNumberUtil.isValidNumber(parsedPhoneNumber)) {
            return phoneNumber;
        }
        const phoneNumberRegionCode = PhoneNumberUtil.getRegionCodeForNumber(parsedPhoneNumber);
        if (phoneNumberRegionCode === (config !== null && config !== void 0 ? config : exports.defaultPhoneNumberConfig).regionCode) {
            return PhoneNumberUtil.format(parsedPhoneNumber, PhoneNumberFormat.NATIONAL);
        }
        else {
            return PhoneNumberUtil.format(parsedPhoneNumber, PhoneNumberFormat.INTERNATIONAL);
        }
    }
    catch (_a) {
        return phoneNumber;
    }
}
exports.formatPhoneNumber = formatPhoneNumber;
/**
 * Formats to callable number that can be used with 'tel:' HTML links. Adds pauses and hashes if number extension or meeting ID is exist.
 * Returns unmodified phone number with meeting ID if phone number is invalid.
 *
 * @param {string} phoneNumber - Phone number to parse.
 * @param {string} meetingId - Optional conference call (e.g. Zoom) meeting ID for a conference call.
 * @param {string} config - Configuration for formatting. If not passed {@link defaultPhoneNumberConfig} is used.
 * @returns {string} Callable number in E164 with optional extension and meeting ID that can be used with 'tel:' HTML links.
 * @example
 * '500-555-0006 ext. 1234', '56789', { regionCode: 'CH' } would result in '+15005550006,,1234#,,56789#'
 */
function getCallablePhoneNumber(phoneNumber, meetingId, config) {
    var _a;
    const unwantedCharactersRegex = new RegExp('[-\\s]+|#+$', 'g');
    function getNumberWithMeetingId(callableNumber, meetingId) {
        var _a;
        if ((_a = meetingId === null || meetingId === void 0 ? void 0 : meetingId.trim()) !== null && _a !== void 0 ? _a : '' !== '') {
            return (callableNumber += `,,${meetingId === null || meetingId === void 0 ? void 0 : meetingId.trim().replace(unwantedCharactersRegex, '')}#`);
        }
        return callableNumber;
    }
    try {
        const parsedPhoneNumber = PhoneNumberUtil.parse(phoneNumber, (config !== null && config !== void 0 ? config : exports.defaultPhoneNumberConfig).regionCode);
        const valid = PhoneNumberUtil.isValidNumber(parsedPhoneNumber);
        if (!valid) {
            return getNumberWithMeetingId(phoneNumber, meetingId);
        }
        let callableNumber = PhoneNumberUtil.format(parsedPhoneNumber, PhoneNumberFormat.E164);
        const phoneNumberExtension = parsedPhoneNumber.getExtension();
        if ((_a = phoneNumberExtension === null || phoneNumberExtension === void 0 ? void 0 : phoneNumberExtension.trim()) !== null && _a !== void 0 ? _a : '' !== '') {
            callableNumber += `,,${phoneNumberExtension === null || phoneNumberExtension === void 0 ? void 0 : phoneNumberExtension.replace(unwantedCharactersRegex, '')}#`;
        }
        return getNumberWithMeetingId(callableNumber, meetingId);
    }
    catch (_b) {
        return getNumberWithMeetingId(phoneNumber, meetingId);
    }
}
exports.getCallablePhoneNumber = getCallablePhoneNumber;
exports.commonPhoneNumberVersion = '1.1.0';
